import { supabase } from '@/lib/supabase';
import {
  fetchScheduleTemplatesForProjectType,
  resolveDefaultTemplateId,
  type ScheduleTemplatePickerOption,
} from '@/lib/scheduleTemplateManagement';
import type { BuiltInSchedulePreset, ProjectPresetKey, ScheduleTemplateSummary } from './types';

export type { ProjectPresetKey, ScheduleTemplateSummary, BuiltInSchedulePreset };
export type { ScheduleTemplatePickerOption };

export function getBuiltInSchedulePreset(_projectType: string): BuiltInSchedulePreset | null {
  return null;
}

export function isBlankPreset(projectType: string, createDefaultSchedule: boolean): boolean {
  return !createDefaultSchedule || projectType === 'Other';
}

export async function fetchScheduleTemplateSummary(
  projectType: string,
  templateId?: string | null
): Promise<ScheduleTemplateSummary | null> {
  if (projectType === 'Other') return null;

  const presetKey = (
    projectType === 'Development' || projectType === 'Renovation' ? projectType : 'Development'
  ) as ProjectPresetKey;

  try {
    let resolvedId = templateId ?? null;
    if (!resolvedId) {
      resolvedId = await resolveDefaultTemplateId(projectType);
    }
    if (!resolvedId) return null;

    const { data: template, error } = await supabase
      .from('jphph_schedule_templates')
      .select('id, name, project_type, description, is_default')
      .eq('id', resolvedId)
      .eq('is_active', true)
      .maybeSingle();

    if (!error && template) {
      const [{ count: itemCount }, { count: linkCount }] = await Promise.all([
        supabase
          .from('jphph_schedule_template_items')
          .select('id', { count: 'exact', head: true })
          .eq('template_id', template.id),
        supabase
          .from('jphph_schedule_template_links')
          .select('id', { count: 'exact', head: true })
          .eq('template_id', template.id),
      ]);

      return {
        key: presetKey,
        templateId: template.id as string,
        name: template.name as string,
        projectType,
        itemCount: itemCount ?? 0,
        linkCount: linkCount ?? 0,
        source: 'database',
        description: (template.description as string | null) ?? undefined,
        isDefault: template.is_default === true,
      };
    }
  } catch (err) {
    console.warn('[fetchScheduleTemplateSummary] DB lookup failed:', err);
  }

  const builtIn = getBuiltInSchedulePreset(projectType);
  if (builtIn) {
    return {
      key: builtIn.key,
      templateId: null,
      name: builtIn.name,
      projectType: builtIn.project_type,
      itemCount: builtIn.items.length,
      linkCount: builtIn.links.length,
      source: 'builtin',
      description: builtIn.description,
    };
  }

  return null;
}

export async function listScheduleTemplatesForNewProject(
  projectType: string
): Promise<ScheduleTemplatePickerOption[]> {
  if (projectType === 'Other') return [];
  return fetchScheduleTemplatesForProjectType(projectType, { usableOnly: true });
}

export function listPresetOptions(): Array<{
  key: ProjectPresetKey;
  label: string;
  description: string;
}> {
  return [
    {
      key: 'Development',
      label: 'Development',
      description: 'Full development build schedule with stages and dependencies.',
    },
    {
      key: 'Renovation',
      label: 'Renovation',
      description: 'Choose a renovation template or add tasks manually.',
    },
    {
      key: 'Blank',
      label: 'Blank / Custom',
      description: 'Create an empty project and add schedule items manually later.',
    },
  ];
}
